// Gameplay framing (§12.7), pure math so the harness test can run it headless: the bounding circle
// of living beans (plus the local ghost), blended 30% toward the arena center, led by 0.1 × the
// average velocity, at distance clamp(10 + 1.6·R, 18, 32). Portrait screens are narrower than the
// spec's landscape reference, so the distance also grows until the circle fits the horizontal field
// of view (decision logged in docs/decisions/WP7.md).
import { MathUtils, PerspectiveCamera, Vector3 } from 'three';
import { BS, RoundPhase } from '@shared/ids';
import type { MapDef } from '@shared/maps/mapTypes';
import type { BeanView, FrameView } from '../../game/FrameView';

export const FRAMING = {
  minR: 4, base: 10, perR: 1.6, minDist: 18, maxDist: 32, maxDistPortrait: 58,
  centerBlend: 0.3, lead: 0.1, sdMult: 0.9, followDist: 18,
  /** Extra radius (m) kept around the circle when fitting the field of view: a bean's body and hops. */
  fitMargin: 1.6,
  /**
   * Safe area (NDC) every framed bean must project inside when no HUD footprint is known; with one
   * (`HudInsets`, set by the UI) the safe area and the frame's vertical offset follow the HUD.
   */
  safeX: 0.9, safeTop: 0.84, safeBottom: 0.8,
  /** Room (fraction of the viewport height) kept above a framed head for its nameplate. */
  plateFrac: 0.035,
  /** Extra NDC kept above the dial's band (the camera springs lag a field running at the camera). */
  dialMargin: 0.08,
  /** Share of the HUD's top/bottom asymmetry the frame's center moves by (1 = the free band's center). */
  shiftShare: 0.8,
  /**
   * Head height checked above the feet, and the lowest z a bean is kept in view to. A FALLING bean is
   * followed down to just above the void surface (`splashFloor`), so its KO splash is already in
   * frame when it lands instead of the camera catching up afterwards.
   */
  headM: 1.3, fallFloor: -1.2, splashFloor: -2.6, splashLeadSec: 0.35,
  lookHeight: 0.5,
  /** Fast beans (above `leadSpeed` m/s) are also fitted where they will be `leadSec` from now. */
  leadSpeed: 5, leadSec: 0.4,
  /** At the maximum distance the target may slide this far (m) toward a bean that still does not fit. */
  maxSlide: 3.5,
  /**
   * A non-local FALLING bean stops being framed this far (m) outside the map's bounds, and a KO
   * splash further out than `holdLeash` is not held in frame (INT-B: far flings zoomed the whole
   * game out to a tiny arena for over a second).
   */
  fallLeash: 3.5, holdLeash: 5,
} as const;

export const VIEW = {
  landscape: { fovDeg: 35, pitchDeg: 55 },
  portrait: { fovDeg: 45, pitchDeg: 62 },
} as const;

/**
 * §12.7 framing set: living beans plus the local ghost; other ghosts are ignored. A falling bean is
 * still living until its KO commits, so the start of its fall stays in frame (decision logged).
 */
export function framed(b: BeanView, map: MapDef | null = null): boolean {
  if (!b.active) return false;
  if (b.isLocal && b.ghost) return true;
  if (b.out || b.state === BS.GHOST || b.state === BS.RESPAWN) return false;
  // INT-B: a bean flung far out over the void is doomed; the camera stays on the action instead of
  // zooming out after it (its own BONK CAM and the replays show the splash). Not the local bean.
  if (map && !b.isLocal && b.state === BS.FALLING && outsideBounds(map, b.x, b.y) > FRAMING.fallLeash) return false;
  return true;
}

/** How far (m) a sim point lies outside the map's camera bounds (0 inside). */
export function outsideBounds(map: MapDef, x: number, y: number): number {
  const b = map.bounds;
  return Math.max(0, b.minX - x, x - b.maxX, b.minY - y, y - b.maxY);
}

export interface FrameWant { x: number; y: number; dist: number; radius: number; count: number }

/**
 * The HUD's footprint over the gameplay view, as fractions of the viewport (§24.2 additive, set by
 * the UI through `GameRenderer.setHudInsets`): the top bar's height, and at the bottom the tallest
 * element in the center band (the Dizzy Dial; ±`bottomHalfWidth` of the width around the middle)
 * and the height elsewhere along the bottom (corner chips, touch controls).
 */
export interface HudInsets {
  top: number; bottom: number; bottomHalfWidth: number; bottomSide: number;
  /** Side panels covering the full height (e.g. the Podium's final standings card); 0 when absent. */
  left?: number; right?: number;
}

/** Footprints of the shipped HUD (INT-C's layout) until the UI reports its own. */
export const DEFAULT_HUD: Readonly<{ landscape: HudInsets; portrait: HudInsets; podiumLandscape: HudInsets; podiumPortrait: HudInsets }> = {
  landscape: { top: 0.085, bottom: 0.235, bottomHalfWidth: 0.11, bottomSide: 0.06 },
  portrait: { top: 0.12, bottom: 0.4, bottomHalfWidth: 0.5, bottomSide: 0.4 },
  // PODIUM: the title on top and the final-standings card on the right (bottom sheet in portrait).
  podiumLandscape: { top: 0.1, bottom: 0.04, bottomHalfWidth: 0, bottomSide: 0.04, right: 0.28 },
  podiumPortrait: { top: 0.1, bottom: 0.45, bottomHalfWidth: 0.5, bottomSide: 0.45 },
};

/**
 * The safe area in screen NDC (after the frame's offset; each limit is a positive distance from the
 * screen center) and that offset: the look target sits `shift` NDC units above and `shiftX` NDC
 * units left of the screen center, so the action is centered in the area the HUD leaves free
 * instead of under the dial or a side panel.
 */
export interface SafeArea {
  left: number; right: number; top: number; bottomCenter: number; bottomSide: number; centerHalfX: number; shift: number; shiftX: number;
}

export function createSafeArea(): SafeArea {
  return { left: 0, right: 0, top: 0, bottomCenter: 0, bottomSide: 0, centerHalfX: 0, shift: 0, shiftX: 0 };
}

/** The safe area for a HUD footprint (null: the legacy fixed safe area, no offset). */
export function safeAreaFor(h: HudInsets | null, out: SafeArea): SafeArea {
  if (!h) {
    out.left = out.right = FRAMING.safeX;
    out.top = FRAMING.safeTop;
    out.bottomCenter = out.bottomSide = FRAMING.safeBottom;
    out.centerHalfX = 0;
    out.shift = 0;
    out.shiftX = 0;
    return out;
  }
  const left = h.left ?? 0;
  const right = h.right ?? 0;
  out.left = FRAMING.safeX - 2 * left;
  out.right = FRAMING.safeX - 2 * right;
  out.top = 1 - 2 * (h.top + FRAMING.plateFrac);
  // A margin over the dial absorbs the springs' lag when the field runs toward the camera.
  out.bottomCenter = 1 - 2 * h.bottom - FRAMING.dialMargin;
  out.bottomSide = 1 - 2 * Math.max(h.bottomSide, 0.03);
  // A body's width either side of the dial's band also counts as "over the dial".
  out.centerHalfX = h.bottomHalfWidth > 0 ? Math.min(1, 2 * h.bottomHalfWidth + 0.08) : 0;
  out.shift = Math.max(0, h.bottom - h.top) * FRAMING.shiftShare;
  out.shiftX = (right - left) * FRAMING.shiftShare;
  return out;
}

const LEGACY_SAFE = safeAreaFor(null, createSafeArea());

/** Extra points kept in frame for a while (sim x, y, z), e.g. a KO splash. */
export interface HoldPoint { x: number; y: number; z: number; until: number }

/**
 * Distance at which a ground circle of radius `r` around the look target fits a camera with
 * vertical FOV `fovDeg`, aspect `aspect` and pitch `pitchDeg` (horizontal and vertical extents).
 */
export function fitDistance(r: number, fovDeg: number, aspect: number, pitchDeg: number): number {
  const tv = Math.tan((fovDeg * MathUtils.DEG2RAD) / 2);
  const th = tv * aspect;
  const vertical = (r * Math.sin(pitchDeg * MathUtils.DEG2RAD)) / tv;
  const horizontal = r / th;
  return Math.max(vertical, horizontal);
}

/** Writes the wanted framing target and distance for `view` into `out`. */
export function frameBeans(view: FrameView, map: MapDef, aspect: number, portrait: boolean, out: FrameWant): FrameWant {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let vx = 0;
  let vy = 0;
  let n = 0;
  for (const b of view.beans) {
    if (!framed(b, map)) continue;
    if (b.x < minX) minX = b.x;
    if (b.x > maxX) maxX = b.x;
    if (b.y < minY) minY = b.y;
    if (b.y > maxY) maxY = b.y;
    vx += b.vx;
    vy += b.vy;
    n++;
  }
  out.count = n;
  if (n === 0) {
    out.x = map.center.x;
    out.y = map.center.y;
    out.dist = FRAMING.minDist;
    out.radius = FRAMING.minR;
    return out;
  }
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const tx = MathUtils.lerp(cx, map.center.x, FRAMING.centerBlend) + (FRAMING.lead * vx) / n;
  const ty = MathUtils.lerp(cy, map.center.y, FRAMING.centerBlend) + (FRAMING.lead * vy) / n;
  // §12.7's R is the bounding radius about the bounding center; the fit radius is measured from the
  // actual (blended, led) target, so every framed bean is inside the fitted view.
  let rb = 0;
  let r = 0;
  for (const b of view.beans) {
    if (!framed(b, map)) continue;
    rb = Math.max(rb, Math.hypot(b.x - cx, b.y - cy));
    r = Math.max(r, Math.hypot(b.x - tx, b.y - ty));
  }
  const R = Math.max(FRAMING.minR, rb);
  const sd = view.clock.phase === RoundPhase.SUDDEN_DEATH ? FRAMING.sdMult : 1;
  const mode = portrait ? VIEW.portrait : VIEW.landscape;
  const spec = FRAMING.base + FRAMING.perR * R;
  const fit = fitDistance(r + FRAMING.fitMargin, mode.fovDeg, aspect, mode.pitchDeg);
  const max = portrait ? FRAMING.maxDistPortrait : FRAMING.maxDist;
  out.x = tx;
  out.y = ty;
  out.radius = R;
  out.dist = MathUtils.clamp(Math.max(spec * sd, fit), FRAMING.minDist, max);
  return out;
}

const fitCam = new PerspectiveCamera();
const fitPt = new Vector3();
const fitTarget = new Vector3();

let fitSafe: SafeArea = LEGACY_SAFE;

/** Projected extent of one sim point against the safe area (1 = on its border); NDC y in `fitPt`. */
function extent(x: number, y: number, z: number): number {
  fitPt.set(x, z, y).project(fitCam);
  const sy = fitPt.y + fitSafe.shift;
  const sx = fitPt.x - fitSafe.shiftX;
  const ex = sx >= 0 ? sx / Math.max(0.05, fitSafe.right) : -sx / Math.max(0.05, fitSafe.left);
  const bottom = Math.abs(sx) < fitSafe.centerHalfX ? fitSafe.bottomCenter : fitSafe.bottomSide;
  const ey = sy >= 0 ? sy / fitSafe.top : -sy / Math.max(0.05, bottom);
  return Math.max(ex, ey, fitPt.z > 1 ? 9 : 0);
}

/** The worst point of the last `worstExtent` call (sim x, y). */
const worstAt = { x: 0, y: 0 };

/** Worst projected extent of the framed beans (1 = on the safe-area border) for a camera pose. */
function worstExtent(view: FrameView, tx: number, ty: number, dist: number, pitchRad: number, holds: readonly HoldPoint[], map: MapDef | null): number {
  fitTarget.set(tx, FRAMING.lookHeight, ty);
  fitCam.position.set(tx, FRAMING.lookHeight + Math.sin(pitchRad) * dist, ty + Math.cos(pitchRad) * dist);
  fitCam.lookAt(fitTarget);
  fitCam.updateMatrixWorld();
  let worst = 0;
  const consider = (x: number, y: number, z: number): void => {
    const e = extent(x, y, z);
    if (e > worst) {
      worst = e;
      worstAt.x = x;
      worstAt.y = y;
    }
  };
  for (const b of view.beans) {
    if (!framed(b, map)) continue;
    const falling = b.state === BS.FALLING;
    const floor = falling ? FRAMING.splashFloor : FRAMING.fallFloor;
    const feet = Math.max(b.z, floor);
    consider(b.x, b.y, feet);
    // A falling bean's splash point is framed from the start of the fall (it lands ~0.5 s later and
    // the distance spring needs that head start).
    if (falling) consider(b.x + b.vx * FRAMING.splashLeadSec, b.y + b.vy * FRAMING.splashLeadSec, floor);
    consider(b.x, b.y, b.z + FRAMING.headM);
    if (Math.hypot(b.vx, b.vy) > FRAMING.leadSpeed) {
      const t = FRAMING.leadSec;
      consider(b.x + b.vx * t, b.y + b.vy * t, Math.max(b.z + b.vz * t, floor));
    }
  }
  for (const h of holds) consider(h.x, h.y, h.z);
  return worst;
}

/**
 * Pushes `out.dist` back (never past the tier's maximum) until every framed bean's feet and head
 * project inside the safe area (`safe`: the HUD-free band, with the frame's vertical offset) through
 * the real camera (yaw 0, `pitchDeg`, `fovDeg`, `aspect`). The
 * §12.7 formula and the circle fit are approximations that clip beans near the camera on wide maps.
 */
export function refineFit(view: FrameView, out: FrameWant, aspect: number, portrait: boolean, holds: readonly HoldPoint[] = [],
  safe: SafeArea = LEGACY_SAFE, map: MapDef | null = null): FrameWant {
  if (out.count === 0 && holds.length === 0) return out;
  fitSafe = safe;
  const mode = portrait ? VIEW.portrait : VIEW.landscape;
  const max = portrait ? FRAMING.maxDistPortrait : FRAMING.maxDist;
  fitCam.fov = mode.fovDeg;
  fitCam.aspect = aspect;
  fitCam.near = 0.5;
  fitCam.far = 600;
  fitCam.updateProjectionMatrix();
  const pitch = mode.pitchDeg * MathUtils.DEG2RAD;
  for (let i = 0; i < 24 && out.dist < max; i++) {
    const worst = worstExtent(view, out.x, out.y, out.dist, pitch, holds, map);
    if (worst <= 1) break;
    out.dist = Math.min(max, out.dist * (1 + Math.min(0.25, (worst - 1) * 0.6) + 0.01));
  }
  // Capped: slide the target toward whatever still does not fit (a long table's far ends).
  let slid = 0;
  for (let i = 0; i < 12 && slid < FRAMING.maxSlide; i++) {
    const worst = worstExtent(view, out.x, out.y, out.dist, pitch, holds, map);
    if (worst <= 1) break;
    const dx = worstAt.x - out.x;
    const dy = worstAt.y - out.y;
    const d = Math.hypot(dx, dy);
    if (d < 1e-3) break;
    const step = Math.min(0.4, FRAMING.maxSlide - slid);
    out.x += (dx / d) * step;
    out.y += (dy / d) * step;
    slid += step;
  }
  return out;
}
